import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, RotateCcw, Trash2, ExternalLink, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Card, Field, PageTitle, Btn, Toggle, inputCls } from "./ui";
import { TopicIdeas } from "./TopicIdeas";
import { WeeklyIdeas } from "./WeeklyIdeas";

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const STATUS = {
  pending: "bg-zinc-700/40 text-zinc-300", generating: "bg-sky-500/15 text-sky-300",
  done: "bg-emerald-500/15 text-emerald-400", failed: "bg-red-500/15 text-red-400",
};

const nextStart = (items) => {
  const last = items.map((i) => new Date(i.scheduled_at)).sort((a, b) => b - a)[0];
  const d = last && last > new Date() ? new Date(last.getTime() + 24 * 3600e3) : new Date(Date.now() + 24 * 3600e3);
  if (!last || last <= new Date()) d.setHours(10, 0, 0, 0);
  return toLocal(d);
};

const QueueForm = ({ items, onAdded }) => {
  const [topics, setTopics] = useState("");
  const [start, setStart] = useState("");
  const [opt, setOpt] = useState({ interval_hours: 24, words: 900, language: "en", inline_images: 2, featured_image: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!start && items) setStart(nextStart(items)); }, [items, start]);
  const list = topics.split("\n").map((t) => t.trim()).filter(Boolean);

  const queue = async (topicList) => {
    if (!topicList.length) { toast.error("Add at least one topic, one per line"); return false; }
    const d = new Date(start);
    if (Number.isNaN(d.getTime()) || d <= new Date()) { toast.error("Pick a future date and time for the first post"); return false; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai-queue", { topics: topicList, start_at: d.toISOString(), ...opt });
      toast.success(data.added === 1 ? `Queued · goes live ${new Date(data.first).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`
        : `${data.added} posts queued · last goes live ${new Date(data.last).toLocaleDateString()}`);
      setStart(toLocal(new Date(new Date(data.last).getTime() + opt.interval_hours * 3600e3)));
      onAdded();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };
  const submit = async () => { if (await queue(list)) setTopics(""); };
  const addTopic = (t) => setTopics((cur) => (cur.split("\n").map((x) => x.trim()).includes(t) ? cur : `${cur.trim() ? `${cur.trim()}\n` : ""}${t}`));
  const o = (k) => (e) => setOpt({ ...opt, [k]: Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value) });

  return (
    <Card title="Add topics" testid="queue-form">
      <Field label={`Topics — one per line (${list.length})`} hint="Each topic becomes one AI article. The topic is also used as the focus keyword.">
        <textarea rows={7} value={topics} onChange={(e) => setTopics(e.target.value)} className={inputCls} data-testid="queue-topics"
          placeholder={"IPL 2026 betting ID guide\nHow to withdraw winnings fast via UPI\nBest live casino games in India"} />
      </Field>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="First post goes live"><input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className={`${inputCls} [color-scheme:dark]`} data-testid="queue-start" /></Field>
        <Field label="Then publish every">
          <select value={opt.interval_hours} onChange={o("interval_hours")} className={inputCls} data-testid="queue-interval">
            <option value={12}>12 hours</option><option value={24}>Day</option><option value={48}>2 days</option><option value={72}>3 days</option><option value={168}>Week</option>
          </select>
        </Field>
        <Field label="Words"><input type="number" min={300} max={2500} value={opt.words} onChange={o("words")} className={inputCls} data-testid="queue-words" /></Field>
        <Field label="Language">
          <select value={opt.language} onChange={(e) => setOpt({ ...opt, language: e.target.value })} className={inputCls} data-testid="queue-language"><option value="en">English</option><option value="hi">Hindi</option></select>
        </Field>
        <Field label="Gold images inside article">
          <select value={opt.inline_images} onChange={o("inline_images")} className={inputCls} data-testid="queue-inline-images"><option value={0}>None</option><option value={1}>1</option><option value={2}>2</option></select>
        </Field>
        <div className="flex items-end pb-2"><Toggle checked={opt.featured_image} onChange={(v) => setOpt({ ...opt, featured_image: v })} testid="queue-featured" label="Gold featured image" /></div>
      </div>
      <p className="mt-4 text-xs text-zinc-500">Articles are written in the background right away, so you can review and edit them before they go live.</p>
      <Btn onClick={submit} disabled={busy} className="mt-4" data-testid="queue-submit"><Bot size={15} /> {busy ? "Queuing…" : `Queue ${list.length || ""} post${list.length === 1 ? "" : "s"}`}</Btn>
      <TopicIdeas language={opt.language} added={list} onAdd={addTopic} onQueue={(t) => queue([t])} busy={busy} />
    </Card>
  );
};

const QueueRow = ({ q, onChange }) => {
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center" data-testid={`queue-item-${q.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-white">{q.title || q.topic}</p>
        <p className="text-xs text-zinc-500">Goes live {new Date(q.scheduled_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })} · {q.words} words · {q.language.toUpperCase()}</p>
        {q.error && <p className="mt-1 text-xs text-red-400">{q.error}</p>}
      </div>
      <span className={`flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${STATUS[q.status]}`} data-testid={`queue-status-${q.id}`}>
        {q.status === "generating" && <Loader2 size={11} className="animate-spin" />}{q.status === "generating" ? "writing" : q.status === "done" ? "written" : q.status}
      </span>
      <div className="flex gap-1">
        {q.post_id && <Link to={`/admin/blogs/${q.post_id}`} className="p-2 text-zinc-500 hover:text-[#D4AF37]" aria-label="Open post" data-testid={`queue-open-${q.id}`}><ExternalLink size={16} /></Link>}
        {q.status === "failed" && <button onClick={() => act(() => api.post(`/admin/ai-queue/${q.id}/retry`), "Retrying")} className="p-2 text-zinc-500 hover:text-white" aria-label="Retry" data-testid={`queue-retry-${q.id}`}><RotateCcw size={16} /></button>}
        {q.status !== "generating" && <button onClick={() => act(() => api.delete(`/admin/ai-queue/${q.id}`), "Removed from queue")} className="p-2 text-zinc-500 hover:text-red-400" aria-label="Remove" data-testid={`queue-delete-${q.id}`}><Trash2 size={16} /></button>}
      </div>
    </div>
  );
};

export default function AiQueue() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/ai-queue").then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  const active = items?.filter((i) => i.status !== "done").length || 0;
  return (
    <div data-testid="admin-ai-queue">
      <PageTitle title="Bulk AI Posts" sub="Add a list of topics — one AI article is written and scheduled per slot automatically" />
      <WeeklyIdeas onQueued={load} />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] [&>*]:min-w-0">
        <QueueForm items={items} onAdded={load} />
        <Card title={`Queue${items ? ` · ${active} waiting` : ""}`} testid="queue-list">
          {items === null ? <p className="text-sm text-zinc-500">Loading…</p> : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-600" data-testid="queue-empty">No topics queued yet.</p>
          ) : <div className="divide-y divide-white/5">{items.map((q) => <QueueRow key={q.id} q={q} onChange={load} />)}</div>}
        </Card>
      </div>
    </div>
  );
}
